import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import store from "@/store";
import { computed, ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import { toast } from "@/common/utils/toast";

import { formList, formAdd, formDel } from "@/common/api/rest/form/form";

import type { FormType, FormPayloadType } from "@/common/types/form";

import { RegisterTheme } from "@/common/themes";
import { formsForm } from "@/components";
import {
  baseConfirm,
  baseModal,
  baseTable,
  svgIcon,
} from "@/common/components";


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const tableHeader = computed(() => [
  { key: "code", label: t("common.labels.code") },
  { key: "name", label: t("common.labels.name") },
]);

const tableData = ref<FormType[]>([]);

const payload = ref<FormPayloadType | null>(null);
const selected = ref<FormType | null>(null);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const isLoading = ref(false);
const showModalAdd = ref(false);
const showModalDel = ref(false);

const id = computed(() => route.params.id as string);

const delMessage = computed(() => {
  const message = t("form.texts.del_confirm", { concat: selected.value?.code });
  const undo = t("common.texts.undo_message");

  return `${message}<br>${undo}`;
});

function fetchForms() {
  isLoading.value = true;

  formList(id.value, {
    search: search.value,
    limit: limit.value,
    page: page.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        page.value = data.infos.page;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function addItem() {
  showModalAdd.value = true;
}

function addItemCancel() {
  showModalAdd.value = false;
  payload.value = null;
}

function addItemConfirm() {
  if (!payload.value) return;

  showModalAdd.value = false;
  store.commit("SET_LOADING", true);

  formAdd(id.value, payload.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        router.push(`/${id.value}/register/form/${data.form_id}/edit`);
      } else {
        toast({ message: message, type: "error" });
        showModalAdd.value = true;
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
      showModalAdd.value = true;
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function editItem(data: FormType) {
  router.push(`/${id.value}/register/form/${data.form_id}/edit`);
}

function delItem(data: FormType) {
  showModalDel.value = true;
  selected.value = data;
}

function delItemCancel() {
  showModalDel.value = false;
  selected.value = null;
}

function delItemConfirm() {
  if (!selected.value) return;

  showModalDel.value = false;
  store.commit("SET_LOADING", true);

  formDel(id.value, selected.value?.form_id as string)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        selected.value = null;
        fetchForms();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function searchItem(value: string) {
  search.value = value;
  fetchForms();
}

function changePage(value: number) {
  page.value = value;
  fetchForms();
}

onMounted(() => {
  if (id.value) fetchForms();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(RegisterTheme), { "tab-active": "form" }, {
      title: _withCtx(() => [
        _createVNode(_unref(svgIcon), {
          icon: "document",
          size: "2rem"
        }),
        _createTextVNode(" " + _toDisplayString(_unref(t)("common.labels.forms")), 1)
      ]),
      content: _withCtx(() => [
        _createVNode(_unref(baseTable), {
          header: tableHeader.value,
          data: tableData.value,
          loading: isLoading.value,
          limit: limit.value,
          page: page.value,
          pages: pages.value,
          total: total.value,
          searched: searched.value,
          options: true,
          edit: true,
          del: true,
          "onChange:page": changePage,
          onSearching: searchItem,
          "onBtn:add": _cache[0] || (_cache[0] = ($event: any) => (addItem())),
          "onBtn:edit": editItem,
          "onBtn:del": delItem
        }, null, 8, ["header", "data", "loading", "limit", "page", "pages", "total", "searched"])
      ]),
      _: 1
    }),
    _createVNode(_unref(baseModal), {
      show: showModalAdd.value,
      "show-button-confirm": true,
      "button-confirm": _unref(t)('common.labels.confirm'),
      "button-close": _unref(t)('common.labels.cancel'),
      onClose: _cache[2] || (_cache[2] = ($event: any) => (addItemCancel())),
      onConfirm: _cache[3] || (_cache[3] = ($event: any) => (addItemConfirm()))
    }, {
      content: _withCtx(() => [
        _createVNode(_unref(formsForm), {
          title: _unref(t)('common.labels.add'),
          data: payload.value,
          onUpdateData: _cache[1] || (_cache[1] = ($event: any) => (payload.value = $event))
        }, null, 8, ["title", "data"])
      ]),
      _: 1
    }, 8, ["show", "button-confirm", "button-close"]),
    _createVNode(_unref(baseConfirm), {
      show: showModalDel.value,
      title: _unref(t)('form.texts.del_form'),
      message: delMessage.value,
      onCancel: _cache[4] || (_cache[4] = ($event: any) => (delItemCancel())),
      onConfirm: _cache[5] || (_cache[5] = ($event: any) => (delItemConfirm()))
    }, null, 8, ["show", "title", "message"])
  ], 64))
}
}

})